import React, { useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import { useSelector, useDispatch } from 'react-redux'
import { withTranslation } from 'react-i18next'
import PlayButton from './components/PlayButton'
import { AudioFormat } from 'constants/enums'
import FlagButton from './components/FlagButton'
import PlaylistMenu from './components/PlaylistMenu'
import MarketIcons from './components/MarketIcons'
import { SongTitle } from './SongListItem.styles'
import { Pill, MaterialIcon } from '@dsny/dsny-component-library'
import { getStringFromSeconds } from 'utils/dateHelpers'
import { NotificationType } from 'constants/enums'
import { PLAY_TRIGGERS } from 'constants/tracking'
import { AIFilled, ExplicitUnfilled } from '@dsny/dsny-component-library/icons'

import NotificationActions from 'reducers/Notification'
import { thunks as DownloadThunks } from 'reducers/Downloads'
import FlaggedActions, { thunks as FlaggedThunks } from 'reducers/Flagged'
import ReleaseActions from 'reducers/Releases'
import { isReleaseFlagged, thunks as PlayQueueThunks } from 'reducers/PlayQueue'
import { trackEvent } from 'utils/segmentService'

import { VideoIconGray } from 'images'

const SongListItem = ({
  index,
  isIpadView,
  song,
  release,
  selectedSong,
  setSelectedSong,
  trackingSource,
  playSource
}) => {
  const dispatch = useDispatch()
  const playlists = useSelector(({ playList }) => playList?.playlistAbstracts)
  const markets = useSelector(({ market }) => market?.markets)

  const popoverOptions = {
    placement: 'left-end',
    className: 'border border-gray-225'
  }

  const onClickHandler = (e) => {
    e.stopPropagation()
    if (isIpadView) {
      const eHandler = !hovered ? handleMouseEnter : handleMouseLeave
      eHandler(song)
    }
  }

  const handleMouseEnter = useCallback(
    (song) => {
      if (setSelectedSong) {
        setSelectedSong(song)
      }
    },
    [setSelectedSong]
  )

  const handleMouseLeave = useCallback(() => {
    if (setSelectedSong) {
      setSelectedSong(null)
    }
  }, [setSelectedSong])

  const handlePlay = () => {
    if (trackingSource) {
      trackEvent('Click', {
        source: trackingSource.toLowerCase().replace(/_/g, '_'),
        type: 'play_song',
        id: String(song.trackid),
        artist: song.artist || '',
        title: song.title || '',
        ordinal: String(index + 1)
      })
    }

    dispatch(
      PlayQueueThunks.playSong(song, {
        source: playSource,
        trigger: PLAY_TRIGGERS.ROW
      })
    )
  }

  const handleFlag = () => {
    const currentValue = song.flagged
    const wasFlagged = isReleaseFlagged(release)

    dispatch(
      FlaggedThunks.flagSong(
        song.releaseid,
        song.trackid,
        !currentValue,
        () => {
          const updatedSongs = release.songs.map((releaseSong) =>
            releaseSong.trackid === song.trackid
              ? { ...releaseSong, flagged: !currentValue }
              : releaseSong
          )
          const flaggedSongs = updatedSongs.filter((s) => s.flagged)
          const updatedRelease = {
            ...release,
            flagged: flaggedSongs.length > 0,
            songs: updatedSongs
          }

          dispatch(ReleaseActions.toggleReleaseInModal(updatedRelease))
          dispatch(ReleaseActions.updateRelease(updatedRelease))

          if (!wasFlagged && !currentValue) {
            dispatch(FlaggedActions.addFlaggedRelease(updatedRelease))
          } else if (flaggedSongs.length === 0) {
            dispatch(FlaggedActions.removeFlaggedReleaseId(release.releaseid))
          }

          dispatch(
            FlaggedActions.updateFlaggedTrackCountForRelease(
              release.releaseid,
              flaggedSongs.length
            )
          )

          dispatch(
            NotificationActions.broadcastMessage(
              song.artist + ' - ' + song.title,
              !currentValue ? NotificationType.flag : NotificationType.unFlag
            )
          )
        },
        () => {}
      )
    )
  }

  const handleDownload = (format = '') => {
    if (trackingSource) {
      trackEvent('Click', {
        source: trackingSource.toLowerCase().replace(/_/g, '_'),
        type: 'download_song',
        id: String(song.trackid),
        artist: song.artist || '',
        title: song.title || '',
        format: format || 'video',
        ordinal: String(index + 1)
      })
    }

    const downloadSource = trackingSource
      ? `${trackingSource.toLowerCase().replace(/ /g, '_')}_song_list`
      : 'release_information_song_list'

    if (song.assettype == 'video') {
      dispatch(DownloadThunks.addVideoToDownloadQueue(song, downloadSource))
    } else {
      dispatch(
        DownloadThunks.addSongToDownloadQueue(song, {
          format,
          source: downloadSource
        })
      )
    }
  }

  const { artist, title, duration, version, assettype, candownload } = song
  const hovered = selectedSong && selectedSong.trackid === song.trackid
  const versionText = version && version.length > 0 ? `(${version})` : ''

  return (
    <div
      className={`w-full flex justify-between items-center w-full px-10 py-3 ${
        hovered ? 'bg-gray-350' : ''
      }`}
      onClick={onClickHandler}
      onMouseEnter={() => handleMouseEnter(song)}
      onMouseLeave={() => handleMouseLeave()}
    >
      <div className="flex flex-row items-center mr-6" style={{ flex: 0.5 }}>
        {assettype === 'audio' && (
          <PlayButton hovered={hovered} onClick={handlePlay} />
        )}
        {assettype === 'audio' && (
          <FlagButton
            hovered={hovered}
            flagged={song.flagged}
            onClick={handleFlag}
          />
        )}
      </div>
      <div className="flex flex-2 items-center truncate">
        {song?.explicit && (
          <ExplicitUnfilled width={18} style={{ marginRight: 4 }} />
        )}
        {song?.is_ai_generated && (
          <AIFilled width={18} style={{ marginRight: 4 }} color="currentColor" />
        )}

        <SongTitle>
          {`${index + 1}. ${artist} - ${title} ${versionText}`}
        </SongTitle>

        <MarketIcons song={song} markets={markets} dispatch={dispatch} />
        {assettype === 'video' && (
          <img
            src={VideoIconGray}
            className="ml-3"
            style={{ width: '24px', height: '16px' }}
            alt="Video Icon"
          />
        )}
      </div>
      <div className="flex-2 text-center">
        {assettype === 'audio' ? `${getStringFromSeconds(duration)}` : ''}
      </div>
      {candownload && (
        <div className="flex" style={{ flex: 2 }}>
          <Pill
            variant="elevated"
            mode="light"
            label="WAV"
            style={{ marginRight: 16 }}
            icon={<MaterialIcon name="FileDownload" style={{ fontSize: 14 }} />}
            onClick={() => handleDownload(AudioFormat.wav?.value)}
          />
          <Pill
            variant="elevated"
            mode="light"
            label="MP3"
            icon={<MaterialIcon name="FileDownload" style={{ fontSize: 14 }} />}
            onClick={() => handleDownload(AudioFormat.mp3?.value)}
          />
        </div>
      )}
      <div className="flex" style={{ flex: 0.5 }}>
        {assettype === 'audio' && (
          <PlaylistMenu
            song={song}
            dispatch={dispatch}
            hovered={hovered}
            playlists={playlists}
            popoverOptions={popoverOptions}
          />
        )}
      </div>
    </div>
  )
}

SongListItem.propTypes = {
  index: PropTypes.number,
  playlists: PropTypes.arrayOf(PropTypes.object),
  isIpadView: PropTypes.bool,
  song: PropTypes.object,
  release: PropTypes.object,
  selectedSong: PropTypes.object,
  setSelectedSong: PropTypes.func,
  trackingSource: PropTypes.string,
  playSource: PropTypes.string
}

SongListItem.defaultProps = {
  playlists: []
}

export default withTranslation()(SongListItem)
